import React from 'react';
import { WorkTabKey, WorkScope } from './WorkTabs';
import WorkAuditComplianceBody from './WorkAuditComplianceBody';
import MySopsTab from '../../../components/governance/sop-instances/MySopsTab';
import StatutoryTab from '../../../components/compliances/StatutoryTab';
import WorkCapaBody, { CapaInvestigation } from './WorkCapaBody';
import WorkTasksBody from './WorkTasksBody';
import WorkMeetingsBody, { GovernanceMeeting } from './WorkMeetingsBody';
import WorkControlTestingBody from './WorkControlTestingBody';
import WorkITATBody from './WorkITATBody';
import WorkRiskAssessmentsBody from './WorkRiskAssessmentsBody';
import WorkProcessWalkthroughsBody from './WorkProcessWalkthroughsBody';
import WorkRenewalsReviewsBody from './WorkRenewalsReviewsBody';
import WorkProjectsBody from './WorkProjectsBody';
import LitWorkBody from './LitWorkBody';
import LeaveApprovals from '../../../components/organization/hr/LeaveApprovals';
import PfmMySubmissions from '../../../components/performanceMeasures/PfmMySubmissions';
import Drawer from '../../../components/common/Drawer';
import '../../../components/layout/ModuleLayout.css';

// Legacy mock data interfaces
interface Task {
  id: number;
  type: string;
  title: string;
  dueDate: string;
  status: string;
  priority: string;
  context: string;
  description: string;
}

interface WorkNetBodyProps {
  activeTab?: WorkTabKey;
  tasks: Task[];
  inboxLoading?: boolean;
  apiTasks: any[];
  activeTasks: any[];
  completedTasks: any[];
  tasksLoading: boolean;
  tasksError: string | null;
  capaInvestigations: CapaInvestigation[];
  capaLoading: boolean;
  capaError: string | null;
  meetings: GovernanceMeeting[];
  meetingsLoading: boolean;
  meetingsError: string | null;
  asideOpen?: boolean;
  onAsideToggle?: () => void;
  onSelectTask?: (task: Task) => void;
  onSelectApiTask?: (task: any) => void;
  onSelectMeeting?: (meeting: GovernanceMeeting) => void;
  onSelectCapaId?: (id: number) => void;
  onCreateTask?: () => void;
  // 'archive' ⇒ History: each body queries closed records and renders read-only.
  scope?: WorkScope;
  readOnly?: boolean;
}

const WorkNetBody: React.FC<WorkNetBodyProps> = ({
  activeTab = 'tasks',
  tasks,
  inboxLoading = false,
  apiTasks,
  activeTasks,
  completedTasks,
  tasksLoading,
  tasksError,
  capaInvestigations,
  capaLoading,
  capaError,
  meetings,
  meetingsLoading,
  meetingsError,
  asideOpen = false,
  onAsideToggle,
  onSelectTask = () => {},
  onSelectApiTask = () => {},
  onSelectMeeting = () => {},
  onSelectCapaId = () => {},
  onCreateTask = () => {},
  scope = 'active',
  readOnly = false,
}) => {
  const filterTasks = (filter: string) => {
    if (filter === 'ALL' || filter === 'AUDIT' || filter === 'COMPLIANCE' || filter === 'CAPA') {
      return tasks.filter(t => {
        if (filter === 'ALL') return true;
        if (filter === 'AUDIT') return t.type === 'AUDIT_QUERY';
        if (filter === 'COMPLIANCE') return t.type === 'COMPLIANCE';
        if (filter === 'CAPA') return t.type === 'CAPA';
        return false;
      });
    }
    return tasks;
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'audit':
        return <WorkAuditComplianceBody filter="audit" tasks={filterTasks('AUDIT')} onSelectTask={onSelectTask} scope={scope} readOnly={readOnly} />;
      case 'compliance':
        // Compliance Process now jumps straight to invoked (initiated) SOPs.
        // The "My SOPs" catalogue moved to the Identity page.
        return <MySopsTab view="instances" scope={scope} readOnly={readOnly} />;
      case 'statutory':
        return <StatutoryTab scope={scope} readOnly={readOnly} />;
      case 'control-testing':
        return <WorkControlTestingBody scope={scope} readOnly={readOnly} />;
      case 'it-assessment':
        return <WorkITATBody scope={scope} readOnly={readOnly} />;
      case 'risk-assessments':
        return <WorkRiskAssessmentsBody scope={scope} readOnly={readOnly} />;
      case 'process-walkthroughs-design':
        return <WorkProcessWalkthroughsBody variant="Design" scope={scope} readOnly={readOnly} />;
      case 'process-walkthroughs-compliance':
        return <WorkProcessWalkthroughsBody variant="Compliance" scope={scope} readOnly={readOnly} />;
      case 'renewals':
        return <WorkRenewalsReviewsBody kind="renewal" scope={scope} readOnly={readOnly} />;
      case 'reviews':
        return <WorkRenewalsReviewsBody kind="review" scope={scope} readOnly={readOnly} />;
      case 'capa':
        return <WorkCapaBody investigations={capaInvestigations} loading={capaLoading} error={capaError} onRowClick={onSelectCapaId} readOnly={readOnly} />;
      case 'tasks':
        return (
          <WorkTasksBody
            tasks={apiTasks}
            activeTasks={activeTasks}
            completedTasks={completedTasks}
            loading={tasksLoading}
            error={tasksError}
            onSelectTask={onSelectApiTask}
            onCreateClick={onCreateTask}
            scope={scope}
            readOnly={readOnly}
          />
        );
      case 'meetings':
        return <WorkMeetingsBody meetings={meetings} loading={meetingsLoading} error={meetingsError} onRowClick={onSelectMeeting} scope={scope} readOnly={readOnly} />;
      case 'litigation':
        return <LitWorkBody scope={scope} readOnly={readOnly} />;
      case 'projects':
        return <WorkProjectsBody scope={scope} readOnly={readOnly} />;
      case 'leave-approvals':
        return <LeaveApprovals />;
      case 'performance-measures':
        return <PfmMySubmissions scope={scope === 'archive' ? 'archive' : 'open'} readOnly={readOnly} />;
      default:
        return null;
    }
  };

  return (
    <div className="module-layout">
      <div className="module-main">
        <div className="tab-content">
          <section role="tabpanel" className="tab-panel show">
            {renderPanel()}
          </section>
        </div>
      </div>
      <Drawer isOpen={asideOpen} onClose={onAsideToggle ?? (() => {})}>
        <div className="aside-card">
          <h3>Work Center Overview</h3>
          <p>• <strong>Inbox</strong> — All pending items</p>
          <p>• <strong>Audit</strong> — Audit queries</p>
          <p>• <strong>Compliance</strong> — Compliance tasks</p>
          <p>• <strong>CAPA</strong> — Investigations</p>
          <p>• <strong>Tasks</strong> — Action items</p>
          <p>• <strong>Meetings</strong> — Scheduled meetings</p>
        </div>
        <div className="aside-card">
          <h3>Task Status</h3>
          <p>• <strong>Open</strong> — Waiting for maker to submit</p>
          <p>• <strong>In Progress</strong> — Submitted, awaiting approver</p>
          <p>• <strong>Pending Ratification</strong> — Approved, awaiting ratifier</p>
          <p>• <strong>Done</strong> — Fully completed</p>
        </div>
      </Drawer>
    </div>
  );
};

export default WorkNetBody;
